import {
	Component,
	computed,
	inject,
	input,
	OnDestroy,
	OnInit,
	signal,
	untracked,
	ElementRef,
	AfterViewInit,
	ViewChild,
	ChangeDetectorRef,
	effect,
} from '@angular/core';
import {
	trigger,
	transition,
	style,
	animate,
} from '@angular/animations';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatDividerModule } from '@angular/material/divider';
import { MatListModule } from '@angular/material/list';
import { MatButtonModule } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import {
	PagedRequestDto,
	RoleDto,
	RolesClient,
	UserCompaniesClient,
	UserCompanyListDto,
} from 'app/core/services/api.service';
import { combineLatest, map, Observable, Subject, takeUntil } from 'rxjs';
import { AuthService } from 'app/core/auth/auth.service';
import { NotificationService } from 'app/core/services/notification/notification.service';
import { NgClass } from '@angular/common';
import { UserService } from 'app/core/user/user.service';
import { MultiTenantStore } from 'app/store/multi-tenant.store';
import { HasPermissionDirective } from 'app/directives/has-permission.directive';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AddTenantComponent } from './add-tenant/add-tenant.component';
import { environment } from 'environments/environment';

@Component({
	selector: 'cm-company-switcher',
	standalone: true,
	imports: [
		MatFormFieldModule,
		MatSelectModule,
		MatDividerModule,
		MatListModule,
		MatButtonModule,
		FormsModule,
		MatIconModule,
		MatTooltipModule,
		MatMenuModule,
		NgClass,
		HasPermissionDirective,
	],
	templateUrl: './company-switcher.component.html',
	animations: [
		trigger('fadeIn', [
			transition(':enter', [
				style({ opacity: 0 }),
				animate('200ms ease-out', style({ opacity: 1 })),
			]),
		]),
	],
})
export class CompanySwitcherComponent
	implements OnInit, OnDestroy, AfterViewInit
{
	isCollapsedSidebar = input<boolean>();
	isNarrow = false;
	isHovering = false; // Track hover state to hide icon during transition
	private resizeObserver?: ResizeObserver;
	private mutationObserver?: MutationObserver;
	private navigationElement?: Element;

	@ViewChild('tenantContainer', { static: false })
	private tenantContainerRef!: ElementRef;

	private readonly _userCompaniesClient = inject(UserCompaniesClient);
	private readonly _authService = inject(AuthService);
	private readonly _notificationService = inject(NotificationService);
	private readonly _userService = inject(UserService);
	private readonly _multiTenantStore = inject(MultiTenantStore);
	private readonly _dialog = inject(MatDialog);
	private readonly _rolesClient = inject(RolesClient);
	private readonly _unsubscribeAll = new Subject<any>();

	toggleSwitcher = signal(false);
	roles = signal<RoleDto[]>([]);

	companies = computed(() => this._multiTenantStore.companies());
	currentTenant = computed(() => this._userService.userSig$().tenantId);
	primaryTenant = computed(() => this._userService.userSig$().primaryTenant);

	/**
	 * Returns the name of the current tenant, or undefined if not found.
	 */
	tenantName = computed(() => {
		const companies = untracked(() => this._multiTenantStore.companies());
		return companies.find((c) => c.companyId === this.currentTenant())
			?.company.name;
	});
	/**
	 * Returns true if more than one company is available for switching.
	 */
	enableSwitcher = computed(
		() => this._multiTenantStore.companies().length > 1
	);

	constructor(private el: ElementRef, private cdr: ChangeDetectorRef) {
		this.setupSwitcherEffect();
	}

	/**
	 * Sets up an effect to initialize or clear the observers
	 * based on whether the switcher is enabled.
	 */
	private setupSwitcherEffect(): void {
		effect(() => {
			const isEnabled = this.enableSwitcher();
			setTimeout(() => {
				if (isEnabled) {
					this.initResizeObserver();
				} else {
					this.clearObservers();
				}
			}, 500);
		});
	}

	ngOnInit(): void {
		combineLatest([
			this._userCompaniesClient.getUserCompanies(),
			this.getRoles(),
		])
			.pipe(takeUntil(this._unsubscribeAll))
			.subscribe(([companies, roles]) => {
				this._multiTenantStore.setCompanies(companies);
				this.roles.set(roles);
			});
	}

	/**
	 * Initializes observers for the tenant container.
	 * Uses both ResizeObserver and MutationObserver to detect navigation state changes.
	 */
	private initResizeObserver(): void {
		this.clearObservers();
		if (this.tenantContainerRef) {
			// ResizeObserver for direct width changes
			this.resizeObserver = new ResizeObserver((entries) => {
				for (const entry of entries) {
					const width = entry.contentRect.width;
					this.isNarrow = width <= 100;
					this.cdr.detectChanges();
				}
			});
			this.resizeObserver.observe(this.tenantContainerRef.nativeElement);

			// Find the parent navigation element and watch for hover class
			this.navigationElement = this.findNavigationElement();
			if (this.navigationElement) {
				this.mutationObserver = new MutationObserver((mutations) => {
					for (const mutation of mutations) {
						if (mutation.attributeName === 'class') {
							this.updateNarrowState();
						}
					}
				});
				this.mutationObserver.observe(this.navigationElement, {
					attributes: true,
					attributeFilter: ['class'],
				});
				// Initial check
				this.updateNarrowState();
			}
		} else {
			console.warn('tenantContainerRef not found!');
		}
	}

	/**
	 * Finds the parent fuse-vertical-navigation element.
	 */
	private findNavigationElement(): Element | undefined {
		let element = this.el.nativeElement as Element;
		while (element) {
			if (element.tagName?.toLowerCase() === 'fuse-vertical-navigation') {
				return element;
			}
			element = element.parentElement as Element;
		}
		return undefined;
	}

	/**
	 * Updates the isNarrow state based on navigation hover class.
	 * Adds a delay when expanding to allow the CSS transition to complete.
	 */
	private updateNarrowState(): void {
		if (this.navigationElement) {
			const isDense = this.navigationElement.classList.contains(
				'fuse-vertical-navigation-appearance-dense'
			);
			const isHovered = this.navigationElement.classList.contains(
				'fuse-vertical-navigation-hover'
			);
			const isOverMode = this.navigationElement.classList.contains(
				'fuse-vertical-navigation-mode-over'
			);

			// Update hover state immediately (used to hide icon during transition)
			this.isHovering = isHovered;

			// Calculate the new narrow state
			const shouldBeNarrow = isDense && !isHovered && !isOverMode;

			// If transitioning from narrow to expanded, add delay for CSS transition
			if (this.isNarrow && !shouldBeNarrow) {
				setTimeout(() => {
					this.isNarrow = shouldBeNarrow;
					this.cdr.detectChanges();
				}, 300); // Delay to allow CSS transition to complete
			} else {
				// Immediately collapse when hovering out
				this.isNarrow = shouldBeNarrow;
				// Close the dropdown when collapsing
				if (shouldBeNarrow) {
					this.toggleSwitcher.set(false);
				}
				this.cdr.detectChanges();
			}
		}
	}

	/**
	 * Disconnects and clears all observers.
	 */
	private clearObservers(): void {
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
			this.resizeObserver = undefined;
		}
		if (this.mutationObserver) {
			this.mutationObserver.disconnect();
			this.mutationObserver = undefined;
		}
	}

	ngAfterViewInit(): void {
		if (this.enableSwitcher()) {
			this.initResizeObserver();
		}
	}

	ngOnDestroy(): void {
		this._unsubscribeAll.next(null);
		this._unsubscribeAll.complete();
		this.clearObservers();
	}

	/**
	 * Fetches the list of roles for the current user.
	 */
	getRoles(): Observable<RoleDto[]> {
		const request = PagedRequestDto.fromJS({
			pageSize: environment.pageSize,
			page: 0,
		});
		return this._rolesClient.getList(request).pipe(map((x) => x.records));
	}

	/**
	 * Handles company selection and triggers tenant switch using Auth0.
	 * @param item The selected company item
	 */
	onSelectCompany(item: UserCompanyListDto): void {
		this._authService
			.switchTenant(item.companyId)
			.pipe(takeUntil(this._unsubscribeAll))
			.subscribe({
				next: (success) => {
					if (success) {
						location.replace('/dashboard');
					}
				},
				error: () => {
					this._notificationService.open({
						title: 'Failed to switch company',
						subtitle:
							'Unable to switch to the selected company. Please contact the company administrator or try again.',
						type: 'error',
					});
				},
			});
	}

	/**
	 * Opens the dialog to create a new tenant.
	 */
	onCreateTenant(): void {
		const input = {
			roles: this.roles(),
			submitLabel: 'Save',
		};
		this._dialog.open(AddTenantComponent, { data: input });
	}
}
